import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import SendIcon from '@atlaskit/icon/core/send';
import Button from '@atlaskit/button/new';
import { CustomItem } from '@atlaskit/menu';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { SuggestedUser } from './SuggestedCollaboratorsDialog';

const i18n = defineMessages({
	shareButtonText: {
		id: 'team-presence.menu.empty-state.suggestions-dialog.share-button',
		defaultMessage: 'Share',
		description: 'Call to action button to share the content with others.',
	},
	shareButtonClickedText: {
		id: 'team-presence.menu.empty-state.suggestions-dialog.share-button.clicked',
		defaultMessage: 'Sent',
		description: 'Button text to indicate that the content has been shared.',
	},
});

const FlexTruncateStyles = xcss({ flexGrow: '1', minWidth: '0' });

export type SuggestedCollaboratorsDialogMenuItemProps = {
	suggestedUser: SuggestedUser;
	onHandleShare: () => void;
	contentId: string;
	contentType: string;
	contentMode: string;
};

export const SuggestedCollaboratorsDialogMenuItem = ({
	suggestedUser,
	onHandleShare,
	contentId,
	contentType,
	contentMode,
}: SuggestedCollaboratorsDialogMenuItemProps) => {
	const { formatMessage } = useIntl();
	const [hasShared, setHasShared] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnShareClicked = () => {
		setHasShared(true);
		onHandleShare();
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'sentText',
				attributes: {
					contentId,
					contentType,
					contentMode,
				},
			},
		}).fire();
	};
	return (
		<CustomItem
			component={() => (
				<Box paddingInline="space.200" paddingBlock="space.100">
					<Flex gap="space.150" alignItems="center">
						<Avatar size="small" src={suggestedUser.picture} />
						<Box xcss={FlexTruncateStyles}>{suggestedUser.name}</Box>
						{hasShared ? (
							<Box paddingInline="space.200" paddingBlock="space.075">
								<Flex gap="space.075" alignItems="center">
									<CheckMarkIcon label="share-success" color={token('color.icon.success')} />
									<Text weight="medium" color="color.text.subtle">
										{formatMessage(i18n.shareButtonClickedText)}
									</Text>
								</Flex>
							</Box>
						) : (
							<Button
								iconBefore={SendIcon}
								testId="share-with-people"
								onClick={handleOnShareClicked}
								spacing="compact"
							>
								{formatMessage(i18n.shareButtonText)}
							</Button>
						)}
					</Flex>
				</Box>
			)}
		/>
	);
};
